import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { EmberTutorialPrompt } from "game-common/models"

import { BUTTON_SIZES, HUD_PANEL_TEXT, HUD_PANEL_TEXT_MUTED } from "./ui_tokens"
import { TextRenderer } from "../text/text_renderer"
import { createButton } from "./button_factory"
import { UiButton } from "./ui_button"
import { BOARD_FRAME, drawBoard, GOLD, RIM_LIGHT } from "./build_board"

// The first-run ember tutorial invitation: a small board low on the screen that says the Lord has
// something to show you, with "Skip" / "Show me". It is deliberately NOT a modal -- it hugs its
// content and leaves the camp fully visible, because the Lord is already speaking behind it.
//
// When the countdown runs out the bar resolves itself to ACCEPT (the timeout is a way to get the
// invitation off the screen, not a way to decline it) and reports that with `reason: "timeout"` so
// the server can tell an ignored prompt from a chosen one.
//
// THE LOOK (2026-10-10). It is build_board's cut-timber board -- the crafting screen's and the build
// palette's frame, rivets and lit edge -- because it is the first panel a new player ever meets and it
// introduces the clues that follow it (gui/clue_callout.ts), whose gold spark it carries. It used to be
// a rounded "dark glass" bar with a bright amber rim and a faint glow, a GREEN "Show me" (a dashboard's
// go colour, and the only green on the camp's HUD) beside a grey "Skip", and a countdown drawn along
// the bottom of the whole bar that never said which answer it was counting toward. The countdown now
// drains ON the "Show me" button, exactly as the chat bubble's "Next" counts down its own
// auto-advance (sprites/avatar_hud.ts): the button that will be pressed for you is the one running out.
//
// Motion runs off GuiManager.updateTimer (update), not a Fader's setInterval.

export type TutorialPromptChoice = "accept" | "skip" | "continue_story" | "begin_chapter"
export type TutorialPromptReason = "click" | "timeout"

const PANEL_W = 300
const PAD_X = 20
const PAD_TOP = 16
const PAD_BOTTOM = 15
const TEXT_X = PAD_X
const TEXT_W = PANEL_W - PAD_X * 2

// The overline: the server's title, set in letterspaced capitals beside the clues' gold spark.
const SPARK_R = 3.5
const SPARK_GAP = 6
const OVERLINE_SPACING = 1.6
const OVERLINE_COLOR = RIM_LIGHT
const OVERLINE_BODY_GAP = 5
const BODY_COLOR = HUD_PANEL_TEXT

// The buttons, right-aligned on their own row: the quiet way out first, the answer last.
const BUTTON_H = BUTTON_SIZES.small.height
const ACCEPT_W = 92
const SKIP_W = 64
const BUTTON_GAP = 8
// Clear air between the last line of copy and the button row.
const BODY_BUTTONS_GAP = 14
const ACCEPT_X = PANEL_W - PAD_X - ACCEPT_W
const SKIP_X = ACCEPT_X - BUTTON_GAP - SKIP_W

// The countdown along the bottom of "Show me", as on the chat bubble's Next button.
const PROGRESS_H = 2
const PROGRESS_ROW = 4 // the bar plus its clearance from the button's bottom border
const PROGRESS_INSET = 5 // keeps the bar clear of the chamfer (UiButton cuts min(5, h / 4))
const PROGRESS_ALPHA = 0.75

// Fallback line heights, used only where a Text measures as zero (a headless/mocked canvas): the
// panel must never collapse onto its own buttons just because nothing could be measured.
const TITLE_LINE_H = 12
const BODY_LINE_H = 17

// Arrival and departure. It rises the last few pixels into place as it fades in, and drops a little
// as it goes -- a board being set down and taken away, not a dialog popping.
const ENTER_MS = 200
const LEAVE_MS = 140
const ENTER_RISE = 8
const LEAVE_DROP = 4

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const clamp01 = (t: number) => Math.max(0, Math.min(1, t))

const spark = (g: Graphics, cx: number, cy: number, r: number) => {
    const k = r * 0.3
    g.moveTo(cx, cy - r)
        .lineTo(cx + k, cy - k).lineTo(cx + r, cy)
        .lineTo(cx + k, cy + k).lineTo(cx, cy + r)
        .lineTo(cx - k, cy + k).lineTo(cx - r, cy)
        .lineTo(cx - k, cy - k)
        .closePath()
}

export interface TutorialPromptProps {
    onChoose?: (choice: TutorialPromptChoice, reason: TutorialPromptReason) => void
}

type Motion = "hidden" | "entering" | "shown" | "leaving"

export class TutorialPrompt extends Container {
    private onChoose?: (choice: TutorialPromptChoice, reason: TutorialPromptReason) => void
    private bg: Graphics
    private textLayer: Container
    private buttonLayer: Container
    private progress: Graphics
    private accept?: UiButton
    private skip?: UiButton

    // Height the current copy needs (see setPrompt). Everything that touches the bottom edge --
    // the board, the button row -- reads it rather than a constant.
    private panelH: number = PAD_TOP + TITLE_LINE_H + OVERLINE_BODY_GAP + BODY_LINE_H + BODY_BUTTONS_GAP + BUTTON_H + PAD_BOTTOM

    // Countdown state. `remainingMs` only ticks while the bar is open; at 0 it resolves to accept.
    private totalMs: number = 0
    private remainingMs: number = 0
    private progressWidth = -1

    private acceptChoice: TutorialPromptChoice = "accept"
    private acceptWidth = ACCEPT_W
    private opened: boolean = false
    private motion: Motion = "hidden"
    private motionMs = 0

    constructor(props: TutorialPromptProps = {}) {
        super()
        this.onChoose = props.onChoose

        // Swallow pointer events over the bar so a click on it never falls through to a world
        // attack (the buttons handle their own clicks; this covers the text/gaps).
        this.interactive = true
        this.hitArea = new Rectangle(0, 0, PANEL_W, this.panelH)
        this.on("pointerdown", (event: any) => event.stopPropagation())
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.on("contextmenu", (event: any) => {
            event.preventDefault()
            event.stopPropagation()
        })

        this.bg = new Graphics()
        this.addChild(this.bg)
        this.textLayer = new Container()
        this.addChild(this.textLayer)
        this.buttonLayer = new Container()
        this.addChild(this.buttonLayer)
        // Over the buttons, so the bar sits on the face of "Show me".
        this.progress = new Graphics()
        this.addChild(this.progress)

        this.drawBackground()
        this.visible = false
    }

    // On-stage footprint, so GuiManager can place the bar without reaching into pixi's measured
    // bounds (which include the dropped shadow).
    public static stageWidth(): number {
        return PANEL_W
    }

    // Instance-side, not static: the bar's height depends on the copy the server sent, so whoever
    // anchors it to the bottom of the screen has to re-read this after setPrompt.
    public stageHeight(): number {
        return this.panelH
    }

    private drawBackground() {
        const g = this.bg
        g.clear()
        drawBoard(g, PANEL_W, this.panelH)
    }

    // The countdown: a gold line along the bottom of "Show me" that runs out as the bar waits. Only
    // repainted when its width moves by a whole pixel.
    private drawProgress() {
        const g = this.progress
        const accept = this.accept
        const full = this.acceptWidth - PROGRESS_INSET * 2
        const pct = this.totalMs > 0 ? clamp01(this.remainingMs / this.totalMs) : 0
        const width = accept ? Math.round(full * pct) : 0
        if (width === this.progressWidth) {
            return
        }
        this.progressWidth = width
        g.clear()
        if (!accept || width <= 0) {
            return
        }
        g.beginFill(GOLD, PROGRESS_ALPHA)
        g.drawRect(accept.x + PROGRESS_INSET, accept.y + BUTTON_H - PROGRESS_ROW, width, PROGRESS_H)
        g.endFill()
    }

    private makeButton(label: string, x: number, y: number, width: number, choice: TutorialPromptChoice): UiButton {
        const primary = choice !== "skip"
        const button = createButton({
            label, width, height: BUTTON_H, size: "small",
            theme: "ember", shape: "chamfer",
            variant: primary ? "primary" : "secondary",
            accent: primary ? GOLD : undefined,
            textColor: primary ? HUD_PANEL_TEXT : HUD_PANEL_TEXT_MUTED,
            bold: primary,
            onClick: () => this.choose(choice, "click"),
        })
        button.position.set(x, y)
        this.buttonLayer.addChild(button)
        return button
    }

    private clearHover() {
        this.accept?.resetInteraction()
        this.skip?.resetInteraction()
    }

    // Answer once and only once: the first choice closes the bar, so a click landing in the same
    // frame the countdown expires can't send two answers.
    private choose(choice: TutorialPromptChoice, reason: TutorialPromptReason) {
        if (!this.opened) {
            return
        }
        this.opened = false
        this.onChoose?.(choice, reason)
    }

    // Rebuild the bar around a server-authored prompt (copy and countdown both come from the
    // server, so the wording can be retuned without a client release).
    public setPrompt(prompt: EmberTutorialPrompt) {
        this.textLayer.removeChildren().forEach(child => child.destroy({ children: true, texture: true, baseTexture: true }))
        this.buttonLayer.removeChildren().forEach(button => button.destroy({ children: true }))
        this.accept = undefined
        this.skip = undefined

        // The spark, on the overline's centre.
        const sparkG = new Graphics()
        this.textLayer.addChild(sparkG)

        // Both lines wrap inside the bar rather than running off it (or under the buttons): the
        // wrap width is set at BUILD time so the measured height below is true on the first frame.
        const overlineX = TEXT_X + SPARK_R * 2 + SPARK_GAP
        const title = new TextRenderer()
            .color(OVERLINE_COLOR)
            .role("compactCaption")
            .bold()
            .strokeThickness(0)
            .wordWrap(TEXT_W - (overlineX - TEXT_X))
            .render((prompt.title || "").toUpperCase()) as Text
        title.style.letterSpacing = OVERLINE_SPACING
        title.x = overlineX
        title.y = PAD_TOP
        this.textLayer.addChild(title)
        const titleH = Math.max(title.height, TITLE_LINE_H)
        sparkG.beginFill(GOLD, 1)
        spark(sparkG, TEXT_X + SPARK_R, PAD_TOP + Math.min(titleH, TITLE_LINE_H) / 2, SPARK_R)
        sparkG.endFill()

        const body = new TextRenderer()
            .color(BODY_COLOR)
            .role("label")
            .strokeThickness(0)
            .wordWrap(TEXT_W)
            .render(prompt.body || "")
        body.x = TEXT_X
        body.y = title.y + titleH + OVERLINE_BODY_GAP
        this.textLayer.addChild(body)

        // Grow the panel around the copy, then hang the button row off the BOTTOM of it. However
        // long the server's wording turns out to be, the buttons end up below it, never over it.
        const textBottom = body.y + Math.max(body.height, BODY_LINE_H)
        this.panelH = Math.ceil(textBottom + BODY_BUTTONS_GAP + BUTTON_H + PAD_BOTTOM)
        const buttonsY = this.buttonsTop()

        // Built answer-first, whatever order they sit in on screen: the button layer's first child
        // is the answer, as it always has been.
        this.acceptChoice = prompt.acceptCommand || "accept"
        this.acceptWidth = prompt.acceptCommand === "continue_story" ? 144 : ACCEPT_W
        this.accept = this.makeButton(prompt.acceptLabel || "Show me",
            PANEL_W - PAD_X - this.acceptWidth, buttonsY, this.acceptWidth, this.acceptChoice)
        if (!prompt.hideSkip) {
            this.skip = this.makeButton(prompt.skipLabel || "Skip", SKIP_X, buttonsY, SKIP_W, "skip")
        }

        this.hitArea = new Rectangle(0, 0, PANEL_W, this.panelH)
        this.drawBackground()

        this.totalMs = prompt.acceptCommand === "continue_story" ? 0 : Math.max(0, prompt.timeoutMs || 0)
        this.remainingMs = this.totalMs
        this.progressWidth = -1
        this.drawProgress()
    }

    // Where the button row currently sits, in the bar's own coordinates. Exposed so a test can assert
    // the thing that broke here: that the copy never reaches it.
    public buttonsTop(): number {
        return this.panelH - PAD_BOTTOM - BUTTON_H
    }

    // The bottom of the last line of copy, in the same coordinates.
    public textBottom(): number {
        let bottom = 0
        this.textLayer.children.forEach((child: any) => {
            if (child instanceof Graphics) return
            bottom = Math.max(bottom, child.y + Math.max(child.height, BODY_LINE_H))
        })
        return bottom
    }

    // The inner face's edges, for a test to hold the copy and the buttons inside the frame.
    public innerRect(): Rectangle {
        return new Rectangle(BOARD_FRAME, BOARD_FRAME, PANEL_W - BOARD_FRAME * 2, this.panelH - BOARD_FRAME * 2)
    }

    // Called every frame by GuiManager; deltaTime is in milliseconds (same contract as
    // CountdownTimer.update). Moves the bar in and out, and drains the countdown while it is open.
    // A prompt with timeoutMs 0 never expires on its own. Returns at once while the bar is away.
    public update(deltaTime: number) {
        if (this.motion === "hidden") {
            return
        }
        const dt = Math.max(0, deltaTime || 0)
        this.animate(dt)
        if (!this.opened || this.totalMs <= 0) {
            return
        }
        this.remainingMs = Math.max(0, this.remainingMs - dt)
        this.drawProgress()
        if (this.remainingMs <= 0) {
            this.choose("accept", "timeout")
        }
    }

    private animate(dt: number) {
        if (this.motion === "entering") {
            this.motionMs += dt
            const t = easeOutCubic(clamp01(this.motionMs / ENTER_MS))
            this.alpha = t
            // Written so it lands on +0: pixi's ObservablePoint ignores 0 over -0.
            this.pivot.y = ENTER_RISE * (t - 1)
            if (this.motionMs >= ENTER_MS) {
                this.motion = "shown"
                this.alpha = 1
            }
        } else if (this.motion === "leaving") {
            this.motionMs += dt
            const t = clamp01(this.motionMs / LEAVE_MS)
            this.alpha = 1 - t
            this.pivot.y = 0 - LEAVE_DROP * easeOutCubic(t)
            if (this.motionMs >= LEAVE_MS) {
                this.motion = "hidden"
                this.visible = false
                this.alpha = 1
                this.pivot.y = 0
            }
        }
    }

    public remaining(): number {
        return this.remainingMs
    }

    public isOpen(): boolean {
        return this.opened
    }

    // Reveal, picking up from wherever an interrupted dismiss had got to.
    public fadeIn() {
        this.opened = true
        if (this.motion === "entering" || this.motion === "shown") {
            return
        }
        const from = this.motion === "leaving" ? this.alpha : 0
        this.visible = true
        this.alpha = from
        this.motion = "entering"
        // Start the arrival at the point on its curve the bar is already showing.
        this.motionMs = ENTER_MS * (1 - Math.cbrt(1 - from))
        this.animate(0)
    }

    // Dismiss: fade out and hide once transparent.
    public fadeOut() {
        this.opened = false
        this.clearHover()
        if (this.motion === "hidden" || this.motion === "leaving") {
            return
        }
        // Leave from the alpha the bar has reached, so cutting short an arrival does not flash.
        this.motionMs = LEAVE_MS * (1 - this.alpha)
        this.motion = "leaving"
        this.animate(0)
    }
}
